import { Component, OnInit, ViewChild, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormationService } from '../../../../../shared/src/lib/service/formation.service';
import { ExerciceSheetService } from '../../../../../shared/src/lib/service/exerciceSheet.service';
import { ExerciceService } from '../../../../../shared/src/lib/service/exercice.service';
import { ExerciceQuestionService } from '../../../../../shared/src/lib/service/exerciceQuestion.service';
import { TaskService } from '../../../../../shared/src/lib/service/task.service';
import { Formation } from '../../../../../shared/src/lib/model/formation.model';
import { ExerciceSheet } from '../../../../../shared/src/lib/model/exerciceSheet.model';
import { Exercice } from '../../../../../shared/src/lib/model/exercice.model';
import { Task } from '../../../../../shared/src/lib/model/task.model';
import { HttpParams } from '@angular/common/http';
import { Teacher } from '../../../../../shared/src/lib/model/teacher.model';
import { AuthenticationService } from '../../../../../shared/src/lib/service/authentification.service';
import { DomSanitizer } from '@angular/platform-browser';
import { debounceTime, Subject } from 'rxjs';
import { Location } from '@angular/common';
import { OccurrenceService } from '../../../../../shared/src/lib/service/occurrence.service';
import { Occurrence } from '../../../../../shared/src/lib/model/occurrence.model';
import { ProgressGaugeComponent } from '../../../../../shared/src/lib/feature/progress-gauge/progress-gauge.component';
import { ExerciceList } from '../../../../../shared/src/lib/model/exerciceList.model';

@Component({
  selector: 'app-assign-exercice',
  templateUrl: './assign-exercice.component.html',
  styleUrls: ['./assign-exercice.component.scss']
})
export class AssignExerciceComponent implements OnInit {
  @ViewChild(ProgressGaugeComponent) gaugeComponent!: ProgressGaugeComponent;

  /**
   * Permet l'utilisation en modal sans routing : on passe les paramètres en @Input
   * Si présents, on ne lit pas ActivatedRoute.
   */
  @Input() formationId?: number;
  @Input() typeExercice?: string; // 'onsite' | 'offsite'
  @Input() typeCategory?: string; // catégorie pour onsite
  @Input() occurrenceId?: number; // id d'occurrence optionnel
  /** URL de la session (rapport) : évite de dépendre du chargement async de l'occurrence pour lier la task. */
  @Input() sessionUrl?: string;
  @Input() openInModal: boolean = false; // indique si affiché dans une modal
  @Output() closed = new EventEmitter<void>();

  formation: Formation = null;
  sheets: ExerciceSheet[] = [];
  exerciceList: Exercice[] = [];
  searchTerm: string = '';
  selectedLevel: string;
  selectedDifficulty: string;
  validatedFilter: string = 'uncompleted';
  levels = this.exerciceSheetService.getCecrlLevels();
  difficulties = this.exerciceService.getDifficultyList();
  id: string; // formation id depuis route si pas d'input
  // typeExercice et typeCategory peuvent venir de route OU inputs
  currentUser: Teacher;

  // Stockage de l'état d'affichage des fiches et exercices
  showPreviewMap = new Map<number, boolean>();
  showExercisesMap = new Map<number, boolean>();
  exercisesMap = new Map<number, ExerciceList>();
  // Stockage de l'état de chargement des exercices par fiche
  exercisesLoadingMap = new Map<number, boolean>();
  assignedTasks: Task[] = [];
  
  // Stockage de l'état d'affichage des prévisualisations d'exercices (dans la liste d'exercices)
  showExercisePreviewMap = new Map<number, boolean>();
  // Stockage des exercices complets avec leurs questions
  exerciseDetailsMap = new Map<number, Exercice>();
  // Stockage de l'état de chargement des questions par exercice
  exerciseQuestionsLoadingMap = new Map<number, boolean>();
  showFilterMenu = false;

  next: string;
  prev: string;

  searchSubject = new Subject<string>();

  isCreating = false;
  previewExerciceMap = new Map<number, boolean>();

  occurence: Occurrence;
  content: any;
  minutes_exercices_per_visio: number = 0;
  extraShouldBeAssigned: number = 0; // minutes dynamiques ajoutées au should_be_assigned

  /** Set des exercices actuellement en cours d'assignation / désassignation */
  private exerciseLoading = new Set<number>();

  /** Indique si un exercice est en cours de traitement */
  isExerciseLoading(id: number): boolean {
    return this.exerciseLoading.has(id);
  }

  /** Indique si les questions d'un exercice sont en cours de chargement */
  isExerciseQuestionsLoading(exerciseId: number): boolean {
    return this.exerciseQuestionsLoadingMap.get(exerciseId) ?? false;
  }

  /** Indique si les exercices d'une fiche sont en cours de chargement */
  isSheetExercisesLoading(sheetId: number): boolean {
    return this.exercisesLoadingMap.get(sheetId) ?? false;
  }

  /**
   * Empêche l'unassign si aucune occurence (écran assign avant session)
   * et que retirer l'exercice ferait passer le total assigné sous le seuil requis.
   * Utilise les valeurs déjà calculées dans le ProgressGaugeComponent.
   */
  canUnassign(exercice: Exercice): boolean {
    // Si on est sur une occurrence (assignation contextuelle) on autorise toujours
    if (this.occurence) return true;
    // Si pas encore chargé ou pas de gauge => on n'empêche pas
    if (!this.gaugeComponent) return true;
    const totalAssigned = this.gaugeComponent.totalTime || 0; // durée totale assignée actuelle
    const required = this.gaugeComponent.duration || 0;       // durée cible (should_be_assigned)
    // Si aucun seuil défini, on laisse faire
    if (!required) return true;
    const afterRemoval = totalAssigned - (exercice.duration || 0);
    // Bloquer si après retrait on passe sous le requis
    return afterRemoval >= required;
  }
  constructor(
    private route: ActivatedRoute,
    private formationService: FormationService,
    public exerciceSheetService: ExerciceSheetService,
    public exerciceService: ExerciceService,
    private exerciceQuestionService: ExerciceQuestionService,
    private taskService: TaskService,
    private authService: AuthenticationService,
    private sanitizer: DomSanitizer,
    private location: Location,
    private occurrenceService: OccurrenceService
  ) { }

  ngOnInit() {
    this.currentUser = this.authService.currentUserSubject.value;

    // Déterminer la source des paramètres : Inputs (modal) ou route.
    if (this.formationId) {
      this.id = this.formationId.toString();
    } else {
      this.id = this.route.snapshot.paramMap.get('id')!;
    }

    if (!this.typeExercice) {
      this.typeExercice = this.route.snapshot.paramMap.get('type')!;
    }

    if (!this.typeCategory) {
      this.typeCategory = this.route.snapshot.paramMap.get('category')!;
    }

    const _occurrenceId = this.occurrenceId || this.route.snapshot.params.occurence_id;
    if (_occurrenceId) {
      this.occurrenceService.getObservableOccurenceById(_occurrenceId).subscribe(data => {
        if (data) {
          this.occurence = data;
          // Si les infos formation + minutes sont déjà connues on recalcule
          this.computeExtraShouldBeAssigned();
        }
      })
    }

    if (this.id) {
      this.formationService.getObservableFormationById(this.id).subscribe(formation => {
        if (formation) {
          this.formation = formation;
          this.selectedLevel = this.exerciceSheetService.getCecrlLevelLabel(this.formation?.global_rate || "A1");
          if (this.typeExercice === 'onsite' && this.typeCategory) {
            this.loadExercises(this.typeCategory);
          }
          if (this.typeExercice === 'offsite') {
            this.getAvailableExercice();
          }
          // Charger les infos d'exercices pour récupérer minutes_exercices_per_visio et calculer l'extra
          // Optimisation: si le taux attendu d'exercices est à 0, on évite l'appel API inutile
          if (this.formation.percent_exercice_rate === 0) {
            this.minutes_exercices_per_visio = 0;
            this.extraShouldBeAssigned = 0; // rien à ajouter
          } else {
            this.loadExerciceInfo();
          }
        }
      });
    }

    this.searchSubject.pipe(debounceTime(500)).subscribe(() => {
      if (this.typeExercice === 'onsite' && this.typeCategory) {
        this.loadExercises(this.typeCategory);
      }
      if (this.typeExercice === 'offsite') {
        this.getAvailableExercice();
      }
    });
  }

  /** Charger les fiches en fonction du niveau et de la catégorie */
  loadExercises(type: string) {
    this.sheets = [];
    let params = new HttpParams()
      .set('category', type)
      .set('cecrl', this.selectedLevel)
      .set('is_validate_by_student', this.formation.student.id.toString())
      .set('status', 'published')
      .set('language', this.formation.language_detail.id.toString())
      .set('salt', Math.random().toString())
      .set('ordering', 'title')
      .set('page_size', '20');

    if (this.searchTerm) {
      params = params.set('title', this.searchTerm);
    }

    if (this.validatedFilter === 'completed') {
      params = params.set('filter_completed', this.formation.student.id.toString());
    }
    if (this.validatedFilter === 'uncompleted') {
      params = params.set('filter_uncompleted', this.formation.student.id.toString());
    }

    this.exerciceSheetService.getObservableExerciceSheets(params).subscribe(data => {
      this.sheets = data.results;
      this.next = data.next;
      this.prev = data.previous;
    });
  }

  getAvailableExercice() {
    this.exerciceList = [];
    let params = new HttpParams()
      .set('status', 'published')
      .set('ordering', '-pk')
      .set('page_size', '10')
      .set('type_exercice', this.typeExercice)
      // .set('page', this.nextPage.toString())
      .set('salt', Math.random().toString())
      .set('is_validate_by_student', this.formation.student.id.toString())
      .set('is_todo_by_student', this.formation.student.id.toString());

    // Si c'est un exercice offsite, on filtre par professeur
    if (this.typeExercice === 'offsite') {
      params = params.set('teacher', this.currentUser.id.toString());
      if (this.selectedDifficulty) {
        params = params.set('difficulty', this.selectedDifficulty);
      }
    }

    if (this.searchTerm) {
      params = params.set('title__icontains', this.searchTerm);
    }

    this.exerciceService.getExercices(params).subscribe(data => {
      if (data) {
        this.exerciceList = [...this.exerciceList, ...data.results];
        this.next = data.next;
      }
    });
  }

  loadMore() {
    if (this.next) {
      if (this.typeExercice === 'onsite') {
        this.exerciceSheetService.getExerciceSheetsByUrl(this.next).subscribe(data => {
          if (data) {
            this.sheets = [...this.sheets, ...data.results];
            this.next = data.next ? data.next : null;
          }
        });
      }
      if (this.typeExercice === 'offsite') {
        this.exerciceService.getExercicesByUrl(this.next).subscribe(data => {
          if (data) {
            this.exerciceList = [...this.exerciceList, ...data.results];
            this.next = data.next;
          }
        });
      }
    }
  }

  /** Vérifier si un exercice est déjà assigné */
  isExerciseAssigned(exerciceId: number): boolean {
    return this.assignedTasks.some(task => task.what.id === exerciceId);
  }

  /** Récupérer l'ID de la tâche associée à un exercice */
  getTaskIdForExercise(exerciceId: number): number | null {
    const task = this.assignedTasks.find(task => task.what.id === exerciceId);
    return task ? task.id : null;
  }

  /** Afficher/Masquer la prévisualisation */
  togglePreview(sheetId: number, type: string) {
    if (this.showPreviewMap.size > 0) {
      this.showPreviewMap.clear();
    } else {
      this.showPreviewMap.set(sheetId, !this.showPreviewMap.get(sheetId));
    }
    if (this.showPreviewMap.get(sheetId)) {
      if (type === 'sheet') {
        this.exerciceSheetService.getExerciceSheetById(sheetId).subscribe(data => {
          this.setContent(data);
        });
      }
    }
  }

  /** Afficher/Masquer la liste des exercices */
  toggleExercises(sheetId: number) {
    if (!this.showExercisesMap.get(sheetId)) {
      this.getExercisesForSheet(sheetId);
    }
    this.showExercisesMap.set(sheetId, !this.showExercisesMap.get(sheetId));
  }

  /** Vérifier si une fiche est affichée en prévisualisation */
  isPreviewVisible(sheetId: number): boolean {
    return this.showPreviewMap.get(sheetId) ?? false;
  }

  /** Vérifier si les exercices d'une fiche sont affichés */
  isExercisesVisible(sheetId: number): boolean {
    return this.showExercisesMap.get(sheetId) ?? false;
  }

  /** Récupérer les exercices pour une fiche */
  getExercisesForSheet(sheetId: number) {
    if (!this.exercisesMap.has(sheetId)) {
      // Activer le loader
      this.exercisesLoadingMap.set(sheetId, true);
      
      let params = new HttpParams()
        .set('sheets', sheetId.toString())
        .set('is_validate_by_student', this.formation.student.id.toString())
        .set('is_todo_by_student', this.formation.student.id.toString())
        .set('page_size', '15');

      this.exerciceService.getExercices(params).subscribe({
        next: (data) => {
          this.exercisesMap.set(sheetId, data);
          // Désactiver le loader
          this.exercisesLoadingMap.set(sheetId, false);
        },
        error: () => {
          // Désactiver le loader en cas d'erreur
          this.exercisesLoadingMap.set(sheetId, false);
        }
      });
    }
  }

  loadMoreExercices(sheetId: number) {
    if (this.exercisesMap.has(sheetId)) {
      const exerciceList = this.exercisesMap.get(sheetId);
      if (exerciceList && exerciceList.next) {
        // Activer le loader
        this.exercisesLoadingMap.set(sheetId, true);
        
        this.exerciceService.getExercicesByUrl(exerciceList.next).subscribe({
          next: (data) => {
            if (data) {
              this.exercisesMap.set(sheetId, {
                ...exerciceList,
                results: [...exerciceList.results, ...data.results],
                next: data.next
              });
            }
            // Désactiver le loader
            this.exercisesLoadingMap.set(sheetId, false);
          },
          error: () => {
            // Désactiver le loader en cas d'erreur
            this.exercisesLoadingMap.set(sheetId, false);
          }
        });
      }
    }
  }

  /** Récupérer les exercices stockés pour une fiche */
  getExercises(sheetId: number): Exercice[] {
    return this.exercisesMap.get(sheetId)?.results ?? [];
  }

  /** Attribuer un exercice à l'étudiant */
  assignExercise(exercice: Exercice, sheetId: number) {
    if (this.exerciseLoading.has(exercice.id)) return; // éviter double clic
    this.exerciseLoading.add(exercice.id);
    const _params = {
      title: `Exercice ${this.typeExercice} à faire`,
      task_sender: 'teacher',
      sender_entity_id: this.currentUser.id,
      task_recipient: 'student',
      recipient_entity_id: this.formation.student.id,
      what_entity: 'exercice',
      what_entity_id: exercice.id,
      for_entity: 'formation',
      for_entity_id: this.formation.id
    };

    // Priorité à sessionUrl (passé depuis le rapport) pour éviter une task sans session
    // si l'occurrence n'est pas encore chargée — sinon la liste filtrée par session ne la retrouve pas.
    const sessionUrl = this.sessionUrl || this.occurence?.session?.url;
    if (sessionUrl) {
      _params['session'] = sessionUrl;
    }

    this.taskService.postTask(_params).subscribe({
      next: () => {
        this.exerciceService.getExerciceByIdWithStudent(exercice.id, this.formation.student).subscribe({
          next: (exerciceData) => {
            if (exerciceData) {
              if (this.typeExercice === 'onsite') {
                if (this.exercisesMap.has(sheetId)) {
                  const exercises = this.exercisesMap.get(sheetId).results;
                  if (exercises) {
                    const index = exercises.findIndex(e => e.id === exercice.id);
                    if (index !== -1) {
                      exercises[index] = exerciceData;
                    }
                  }
                }
              }
              if (this.typeExercice === 'offsite') {
                const index = this.exerciceList.findIndex(e => e.id === exercice.id);
                if (index !== -1) {
                  this.exerciceList[index] = exerciceData;
                }
              }
            }
            this.refreshProgress();
          },
          error: () => {
            // TODO: gestion erreur (toast?)
          },
          complete: () => {
            this.exerciseLoading.delete(exercice.id);
          }
        });
      },
      error: () => {
        this.exerciseLoading.delete(exercice.id);
      }
    });
  }

  /** Désassigner un exercice */
  unassignExercise(exercice: Exercice, sheetId?: number) {
    if (this.exerciseLoading.has(exercice.id)) return; // éviter double clic
    this.exerciseLoading.add(exercice.id);
    const params = new HttpParams()
      .set('recipient_model', 'student')
      .set('recipient_entity_id', this.formation.student.id.toString())
      .set('what_model', 'exercice')
      .set('for_model', 'formation')
      .set('for_entity_id', this.formation.id.toString())
      .set('what_entity_id', exercice.id.toString())
      .set('status', 'open');

    this.taskService.getTasks(params).subscribe({
      next: (data) => {
        if (data.results) {
          let pending = data.results.length;
          if (pending === 0) {
            this.exerciseLoading.delete(exercice.id);
          }
          data.results.forEach(task => {
            this.taskService.patchTask(task.id, { status: 'deleted' }).subscribe({
              next: () => {
                this.exerciceService.getExerciceByIdWithStudent(exercice.id, this.formation.student).subscribe({
                  next: (exerciceData) => {
                    if (exerciceData) {
                      if (this.typeExercice === 'onsite') {
                        if (this.exercisesMap.has(sheetId)) {
                          const exercises = this.exercisesMap.get(sheetId).results;
                          if (exercises) {
                            const index = exercises.findIndex(e => e.id === exercice.id);
                            if (index !== -1) {
                              exercises[index] = exerciceData;
                            }
                          }
                        }
                      }
                      if (this.typeExercice === 'offsite') {
                        const index = this.exerciceList.findIndex(e => e.id === exercice.id);
                        if (index !== -1) {
                          this.exerciceList[index] = exerciceData;
                        }
                      }
                    }
                    this.refreshProgress();
                  },
                  complete: () => {
                    pending--;
                    if (pending === 0) {
                      this.exerciseLoading.delete(exercice.id);
                    }
                  },
                  error: () => {
                    pending--;
                    if (pending === 0) {
                      this.exerciseLoading.delete(exercice.id);
                    }
                  }
                });
              },
              error: () => {
                pending--;
                if (pending === 0) {
                  this.exerciseLoading.delete(exercice.id);
                }
              }
            });
          });
        }
      },
      error: () => {
        this.exerciseLoading.delete(exercice.id);
      }
    });
  }

  goBack() {
    if (this.openInModal) {
      this.closed.emit();
    } else {
      this.location.back(); // Revient en arrière dans l'historique du navigateur
    }
  }

  /** Ouvre/Ferme la prévisualisation */
  toggleExercicePreview(exerciseId: number) {
    this.previewExerciceMap.set(exerciseId, !this.previewExerciceMap.get(exerciseId));
  }

  /** Vérifie si une prévisualisation est ouverte */
  isPreviewExerciceVisible(exerciseId: number): boolean {
    return this.previewExerciceMap.get(exerciseId) ?? false;
  }

  reloadList() {
    this.exerciceList = [];
    this.isCreating = false;
    this.getAvailableExercice();
  }

  refreshProgress() {
    if (this.gaugeComponent) {
      this.gaugeComponent.getAssignedExercisesDuration();
    }
  }

  private loadExerciceInfo() {
    if (!this.formation) return;
    if (this.formation.percent_exercice_rate === 0) return; // garde supplémentaire (sécurité)
    this.formationService.getFormationExercicesInfo(this.formation.id).subscribe({
      next: (infos) => {
        this.minutes_exercices_per_visio = infos.minutes_exercices_per_visio || 0;
        this.computeExtraShouldBeAssigned();
      }
    });
  }

  private computeExtraShouldBeAssigned() {
    if (!this.occurence) {
      this.extraShouldBeAssigned = 0;
      return;
    }
    const start = this.occurence.start ? new Date(this.occurence.start) : null;
    const end = this.occurence.end ? new Date(this.occurence.end) : null;
    if (!start || !end) {
      this.extraShouldBeAssigned = 0;
      return;
    }
    const durationMinutes = (end.getTime() - start.getTime()) / 60000;
    this.extraShouldBeAssigned = Math.round(durationMinutes * this.minutes_exercices_per_visio);
  }

  setContent(sheet: ExerciceSheet) {
    if (sheet?.content) {
      sheet.content = sheet.content.sort((a, b) => a.type_content.position - b.type_content.position);
      let tempContent = '';
      for (let i = 0; i < sheet.content.length; i++) {
        if (sheet.content[i].type_content.display_in_content) {
          tempContent += `<div class="${sheet.content[i].type_content.css_class}">${sheet.content[i].value}</div>`;
        }
      }
      this.content = this.sanitizer.bypassSecurityTrustHtml(tempContent);
      // setTimeout(() => {
      //   this.setTtsHandle();
      //   this.initializeGeneratedVocabularyImages();
      // }, 1000);
    }
  }

  /** Met à jour un exercice offsite dans la liste après modification */
  updateOffsiteExerciseInList(updatedExercise: Exercice) {
    this.exerciceService.getExerciceById(updatedExercise.id).subscribe(exercice => {
      if (exercice) {
        const index = this.exerciceList.findIndex(e => e.id === exercice.id);
        if (index !== -1) {
          this.exerciceList[index] = exercice;
          this.togglePreview(exercice.id, 'exercice')
        }
      }
    });
  }

  /** Afficher/Masquer la prévisualisation d'un exercice avec ses questions */
  toggleExercisePreview(exerciseId: number) {
    const isCurrentlyVisible = this.showExercisePreviewMap.get(exerciseId);
    
    if (!isCurrentlyVisible && !this.exerciseDetailsMap.has(exerciseId)) {
      // Charger les détails de l'exercice avec ses questions
      this.loadExerciseDetails(exerciseId);
    }
    
    this.showExercisePreviewMap.set(exerciseId, !isCurrentlyVisible);
  }

  /** Charger les détails complets d'un exercice (avec questions et réponses) */
  private loadExerciseDetails(exerciseId: number) {
    // Activer le loader
    this.exerciseQuestionsLoadingMap.set(exerciseId, true);
    
    // Charger l'exercice de base
    this.exerciceService.getExerciceById(exerciseId).subscribe(exercice => {
      if (exercice) {
        // Charger les questions avec leurs réponses via le service ExerciceQuestionService
        // Utilisation d'un page_size élevé pour charger toutes les questions en une fois
        const params = new HttpParams()
          .set('exercice', exerciseId.toString())
          .set('page_size', '100')
          .set('salt', Math.random().toString());
        
        this.exerciceQuestionService.getExerciceQuestions(params).subscribe({
          next: (questionsData) => {
            if (questionsData && questionsData.results) {
              exercice.questions = questionsData.results;
            }
            this.exerciseDetailsMap.set(exerciseId, exercice);
            // Désactiver le loader
            this.exerciseQuestionsLoadingMap.set(exerciseId, false);
          },
          error: () => {
            // Désactiver le loader en cas d'erreur
            this.exerciseQuestionsLoadingMap.set(exerciseId, false);
          }
        });
      } else {
        this.exerciseQuestionsLoadingMap.set(exerciseId, false);
      }
    });
  }

  /** Vérifier si la prévisualisation d'un exercice est visible */
  isExercisePreviewVisible(exerciseId: number): boolean {
    return this.showExercisePreviewMap.get(exerciseId) ?? false;
  }

  /** Récupérer les détails d'un exercice */
  getExerciseDetails(exerciseId: number): Exercice | undefined {
    return this.exerciseDetailsMap.get(exerciseId);
  }
}
